/**
 * MediaInfoRemapper
 *
 * Maps MediaInfoDto (data layer) ↔ MediaInfoEntity (domain) and raw Firestore data.
 * Raw Firestore keys are `path`/`thumbnail` (Flutter @JsonKey).
 */
import type { MediaInfoDto } from '@/data/dtos/info/mediaInfoDto';
import type { JsonObject } from '@/data/types/json';
import type { MediaInfoEntity } from '@/domain/entities/info/mediaInfoEntity';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';

export class MediaInfoRemapper {
  static fromDto(dto: MediaInfoDto): MediaInfoEntity {
    return {
      type: dto.type,
      mediaId: dto.mediaId,
      firebaseId: dto.firebaseId,
      thumbnail: dto.thumbnail,
      path: dto.path,
      duration: dto.duration,
      aspectRatio: dto.aspectRatio ?? 0,
      allowUnlock: dto.allowUnlock ?? false,
      schedule: dto.schedule,
      allowEarlyAccess: dto.allowEarlyAccess,
      unlockCharge: dto.unlockCharge,
      premiumVideoCharge: dto.premiumVideoCharge,
      boostVideoCharge: dto.boostVideoCharge,
      published: dto.published,
    };
  }

  static fromJson(data: JsonObject): MediaInfoDto {
    return {
      mediaId: data['mediaId'] as string | undefined,
      firebaseId: (data['firebaseId'] as string | undefined) || '',
      path: (data['path'] as string | undefined) || '',
      thumbnail: (data['thumbnail'] as string | undefined) || '',
      type: (data['type'] as number | undefined) ?? 1,
      schedule: data['schedule'] as number | undefined,
      duration: data['duration'] as number | undefined,
      allowEarlyAccess: data['allowEarlyAccess'] as boolean | undefined,
      allowUnlock: (data['allowUnlock'] as boolean | undefined) ?? false,
      unlockCharge: data['unlockCharge'] as number | undefined,
      premiumVideoCharge: data['premiumVideoCharge'] as number | undefined,
      boostVideoCharge: data['boostVideoCharge'] as number | undefined,
      published: data['published'] as boolean | undefined,
      aspectRatio: (data['aspectRatio'] as number | undefined) ?? VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
    };
  }

  static toEntity(data: any): MediaInfoEntity {
    return MediaInfoRemapper.fromDto(MediaInfoRemapper.fromJson(data));
  }

  static toDto(entity: MediaInfoEntity): MediaInfoDto {
    return {
      mediaId: entity.mediaId,
      firebaseId: entity.firebaseId,
      path: entity.path,
      thumbnail: entity.thumbnail,
      type: entity.type,
      schedule: entity.schedule,
      duration: entity.duration,
      allowEarlyAccess: entity.allowEarlyAccess,
      allowUnlock: entity.allowUnlock,
      unlockCharge: entity.unlockCharge,
      premiumVideoCharge: entity.premiumVideoCharge,
      boostVideoCharge: entity.boostVideoCharge,
      published: entity.published,
      aspectRatio: entity.aspectRatio,
    };
  }

  static toJson(dto: MediaInfoDto): JsonObject {
    return {
      mediaId: dto.mediaId ?? null,
      firebaseId: dto.firebaseId,
      thumbnail: dto.thumbnail,
      path: dto.path,
      type: dto.type,
      schedule: dto.schedule ?? null,
      duration: dto.duration ?? null,
      allowEarlyAccess: dto.allowEarlyAccess ?? null,
      allowUnlock: dto.allowUnlock ?? false,
      unlockCharge: dto.unlockCharge ?? null,
      premiumVideoCharge: dto.premiumVideoCharge ?? null,
      boostVideoCharge: dto.boostVideoCharge ?? null,
      published: dto.published ?? null,
      aspectRatio: dto.aspectRatio ?? VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
    };
  }
}
